import { Injectable, computed, inject, signal } from '@angular/core';
import { THEME_CONFIG, type RindTheme } from './theme-config';

/**
 * Light/dark preference. Persists to localStorage and sets `dark-theme` on
 * `<html>` so global `[dark-theme]` token blocks and `:host-context([dark-theme])`
 * apply across the document.
 *
 * Register with {@link provideTheme} so {@link THEME_CONFIG} is available.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly config = inject(THEME_CONFIG);
  private readonly themeSignal = signal<RindTheme>(
    readStoredTheme(this.config.storageKey),
  );

  readonly theme = this.themeSignal.asReadonly();
  readonly isDark = computed(() => this.themeSignal() === 'dark');

  constructor() {
    applyDocumentTheme(this.themeSignal());
  }

  toggle(): void {
    this.set(this.isDark() ? 'light' : 'dark');
  }

  set(theme: RindTheme): void {
    this.themeSignal.set(theme);
    applyDocumentTheme(theme);
    try {
      localStorage.setItem(this.config.storageKey, theme);
    } catch {
      // Private mode / storage blocked — theme still applies for this session.
    }
  }
}

/** Applies theme before Angular boots to avoid a light→dark flash. */
export function applyStoredThemeEarly(storageKey: string): void {
  applyDocumentTheme(readStoredTheme(storageKey));
}

export function readStoredTheme(storageKey: string): RindTheme {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch {
    // ignore
  }

  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

export function applyDocumentTheme(theme: RindTheme): void {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('dark-theme', '');
    root.style.colorScheme = 'dark';
  } else {
    root.removeAttribute('dark-theme');
    root.style.colorScheme = 'light';
  }
}
